/** In-call breaks and warm-transfer controls for interpreters. */
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useSelector } from 'react-redux';
import { makeStyles } from 'tss-react/mui';
import { z } from 'zod';
import Button from '../../base/ui/components/web/Button';
import { BUTTON_TYPES } from '../../base/ui/constants.any';
import { withPixelLineHeight } from '../../base/styles/functions.web';
import { queueService } from '../../interpreter-queue/InterpreterQueueService';
import { apiClient } from '../../shared/api-client';
import { getPersistentJson } from '../../vrs-auth/storage';
import useShiftBreaks, { shiftAccountKey, shiftRequest } from './useShiftBreaks';
const candidatesSchema = z.object({ candidates: z.array(z.object({
        id: z.string().min(1).max(120), languages: z.array(z.string()).optional()
    })) });
const useStyles = makeStyles()(theme => ({
    chip: {
        position: 'fixed', right: theme.spacing(3), bottom: 168, zIndex: 380,
        background: theme.palette.ui01, color: theme.palette.text01,
        border: `1px solid ${theme.palette.ui04}`, borderRadius: theme.shape.borderRadius,
        cursor: 'pointer', minHeight: 48, padding: theme.spacing(2),
        '&:focus-visible': { outline: `2px solid ${theme.palette.focus01}`, outlineOffset: 2 }
    },
    card: {
        position: 'fixed', right: theme.spacing(3), bottom: 224, zIndex: 380,
        background: theme.palette.ui01, color: theme.palette.text01,
        border: `1px solid ${theme.palette.ui04}`, borderRadius: theme.shape.borderRadius,
        padding: theme.spacing(3), width: 304, maxWidth: 'calc(100vw - 32px)',
        maxHeight: 'calc(100dvh - 240px)', overflowY: 'auto', boxSizing: 'border-box',
        overflowWrap: 'anywhere'
    },
    title: { ...withPixelLineHeight(theme.typography.bodyShortBold), margin: '0 0 8px' },
    line: { ...withPixelLineHeight(theme.typography.bodyShortRegular), margin: '0 0 12px' },
    controls: { display: 'flex', flexDirection: 'column', gap: theme.spacing(2), marginBottom: theme.spacing(3) },
    breakAction: { borderWidth: 2, borderColor: theme.palette.focus01 },
    button: { transition: 'none', border: `1px solid ${theme.palette.ui04}`, minHeight: 48, width: '100%', whiteSpace: 'normal', textAlign: 'start',
        '&:focus-visible': { outline: `2px solid ${theme.palette.focus01}`, outlineOffset: 2 } },
    note: { ...withPixelLineHeight(theme.typography.bodyShortRegular), marginTop: theme.spacing(2) },
    docked: { position: 'static', width: '100%', maxHeight: 'none', maxWidth: '100%', marginTop: theme.spacing(2) }
}));
export default function ShiftPanel({ docked = false } = {}) {
    const { classes } = useStyles();
    const matchData = useSelector((state) => state['features/interpreter-queue']?.matchData);
    const room = useSelector((state) => state['features/base/conference'].room);
    const [open, setOpen] = useState(false);
    const shift = useShiftBreaks(open);
    const chip = useRef(null);
    const activeCallId = useMemo(() => {
        if (!room)
            return undefined;
        if (typeof matchData?.callId === 'string' && matchData.callId && matchData.roomName === room)
            return matchData.callId;
        const stored = getPersistentJson('vrs_active_call');
        return typeof stored?.callId === 'string' && stored.callId && stored.roomName === room ? stored.callId : undefined;
    }, [room, matchData?.callId, matchData?.roomName]);
    const owner = useMemo(() => ({ account: shift.account, callId: activeCallId, room }), [shift.account, activeCallId, room]);
    const currentOwner = useRef(owner);
    currentOwner.current = owner;
    const [candidates, setCandidates] = useState({ owner: null, rows: [], status: 'loading' });
    const load = useRef(null);
    const [transferNote, setTransferNote] = useState('');
    const [transferPending, setTransferPending] = useState(false);
    const transfer = useRef(null);
    const clearTransfer = useCallback(() => {
        if (transfer.current)
            clearTimeout(transfer.current.timer);
        transfer.current = null;
        setTransferPending(false);
    }, []);
    const loadCandidates = useCallback(async () => {
        load.current?.abort();
        const controller = new AbortController();
        load.current = controller;
        const owns = () => currentOwner.current === owner && shiftAccountKey() === owner.account
            && load.current === controller && !controller.signal.aborted;
        setCandidates({ owner, rows: [], status: 'loading' });
        try {
            if (!owner.account)
                throw new Error('Interpreter profile unavailable');
            const response = await shiftRequest(apiClient.get('/api/interpreter/transfer-candidates', { signal: controller.signal, retryAuth: false }), controller.signal);
            const parsed = candidatesSchema.safeParse(response.data);
            if (response.error || response.status < 200 || response.status >= 300 || !parsed.success)
                throw new Error('Candidates unavailable');
            if (owns())
                setCandidates({ owner, rows: parsed.data.candidates, status: 'ready' });
        }
        catch {
            if (owns())
                setCandidates({ owner, rows: [], status: 'failed' });
        }
        finally {
            controller.abort();
        }
    }, [owner]);
    useEffect(() => {
        if (open)
            void loadCandidates();
        return () => { load.current?.abort(); load.current = null; };
    }, [open, loadCandidates]);
    useEffect(() => {
        setTransferNote('');
        const matches = (data) => Boolean(owner.account && owner.callId)
            && currentOwner.current === owner && shiftAccountKey() === owner.account && Boolean(data) && typeof data === 'object'
            && data.callId === owner.callId;
        const accepted = (data) => {
            if (!matches(data))
                return;
            clearTransfer();
            const name = typeof data.acceptedByName === 'string' ? data.acceptedByName : '';
            setTransferNote(`Transfer accepted${name ? ` by ${name}` : ''} — you can leave the call.`);
        };
        const cancelled = (data) => {
            if (!matches(data))
                return;
            clearTransfer();
            setTransferNote('Transfer declined — you remain assigned to this call.');
        };
        queueService.on('callTransferAccepted', accepted);
        queueService.on('callTransferCancelled', cancelled);
        return () => {
            clearTransfer();
            queueService.off('callTransferAccepted', accepted);
            queueService.off('callTransferCancelled', cancelled);
        };
    }, [owner, clearTransfer]);
    const requestTransfer = (candidate) => {
        if (transfer.current || !owner.callId || !owner.account || currentOwner.current !== owner || shiftAccountKey() !== owner.account
            || candidates.owner !== owner || candidates.status !== 'ready' || !candidates.rows.includes(candidate))
            return;
        const timer = setTimeout(() => {
            if (transfer.current?.owner === owner && currentOwner.current === owner && shiftAccountKey() === owner.account) {
                setTransferNote('Transfer is still unconfirmed. Stay in this call until the other interpreter accepts.');
            }
        }, 15000);
        transfer.current = { owner, timer };
        setTransferPending(true);
        try {
            queueService.transferCall(owner.callId, candidate.id);
            setTransferNote('Transfer requested. The other interpreter must accept before you leave.');
        }
        catch {
            // The service may have queued the request before throwing; do not send it twice.
            setTransferNote('Transfer could not be confirmed. Stay in this call until the other interpreter accepts.');
        }
    };
    const candidateStatus = candidates.owner === owner ? candidates.status : 'loading';
    const rows = candidates.owner === owner ? candidates.rows : [];
    const closePanel = () => { setOpen(false); chip.current?.focus(); };
    return React.createElement(React.Fragment, null,
        open && React.createElement("section", { "aria-label": 'Shift controls', className: `${classes.card} ${docked ? classes.docked : ''}`, id: 'in-call-shift-panel', onKeyDown: event => { if (event.key === 'Escape') {
                event.stopPropagation();
                closePanel();
            } } },
            React.createElement("h2", { className: classes.title }, "Shift"),
            React.createElement("div", { "aria-live": 'polite', className: classes.line }, shift.status === 'loading' ? 'Loading break status…' : shift.status === 'failed' ? 'Break status could not be loaded.' : shift.line.text),
            React.createElement("div", { "aria-busy": shift.busy || shift.availability.pending, className: classes.controls },
                React.createElement(Button, { className: `${classes.button} ${classes.breakAction}`, type: BUTTON_TYPES.TERTIARY, disabled: shift.busy || shift.availability.pending || !shift.unconfirmed && shift.status !== 'ready', label: shift.busy ? shift.unconfirmed ? 'Checking break…' : shift.line.active ? 'Ending break…' : 'Starting break…' : shift.unconfirmed ? 'Check break status' : shift.line.active ? 'End break' : 'Start break', onClick: shift.unconfirmed ? shift.checkBreak : shift.toggleBreak }),
                React.createElement(Button, { className: classes.button, disabled: shift.busy || shift.unconfirmed, label: 'Refresh break status', onClick: shift.loadBreaks, type: BUTTON_TYPES.TERTIARY }),
                shift.availability.failed && React.createElement(Button, { className: classes.button, disabled: shift.busy || shift.unconfirmed, label: 'Retry status', onClick: shift.retryAvailability, type: BUTTON_TYPES.TERTIARY })),
            React.createElement("div", { "aria-live": 'polite', className: classes.note },
                shift.note,
                " ",
                shift.availability.text),
            React.createElement("h2", { className: classes.title }, "Transfer call"),
            React.createElement("div", { className: classes.controls },
                candidateStatus === 'loading' ? React.createElement("div", { role: 'status' }, "Loading available interpreters\u2026")
                    : candidateStatus === 'failed' ? React.createElement("div", { role: 'status' }, "Available interpreters could not be loaded.")
                        : rows.length ? rows.map((candidate, index) => React.createElement(Button, { className: classes.button, disabled: !activeCallId || transferPending, key: candidate.id, label: `Available interpreter ${index + 1}${candidate.languages?.length ? ` · ${candidate.languages.join(', ')}` : ''}`, onClick: () => requestTransfer(candidate), type: BUTTON_TYPES.TERTIARY }))
                            : React.createElement("div", null, "No other interpreters are available right now."),
                React.createElement(Button, { className: classes.button, label: 'Refresh interpreters', onClick: loadCandidates, type: BUTTON_TYPES.TERTIARY })),
            React.createElement("div", { "aria-live": 'polite', className: classes.note }, transferNote),
            React.createElement(Button, { className: classes.button, label: 'Close shift panel', onClick: closePanel, type: BUTTON_TYPES.TERTIARY })),
        React.createElement("button", { "aria-controls": 'in-call-shift-panel', "aria-expanded": open, "aria-label": 'Shift panel: breaks and call transfer', className: `${classes.chip} ${docked ? classes.docked : ''}`, onClick: () => setOpen(value => !value), ref: chip, type: 'button' }, shift.line.active ? 'On break · Shift controls' : 'Shift and transfer'));
}
